import { forwardRef, Fragment, type ReactNode } from 'react';
import {
  Select as ShadcnSelect,
  SelectContent as ShadcnSelectContent,
  SelectGroup as ShadcnSelectGroup,
  SelectItem as ShadcnSelectItem,
  SelectLabel as ShadcnSelectLabel,
  SelectSeparator as ShadcnSelectSeparator,
  SelectTrigger as ShadcnSelectTrigger,
  SelectValue as ShadcnSelectValue,
} from '@/app/_components/ui/select';
import { cn } from '@/app/_lib/utils';

export type SelectSize = 'sm' | 'default';

export interface SelectOption {
  label: string;
  value: string;
}

export interface SelectGroup {
  label?: string;
  options: SelectOption[];
}

export interface SelectProps {
  /**
   * The controlled value of the select
   */
  value?: string;
  /**
   * Default value for uncontrolled select
   */
  defaultValue?: string;
  /**
   * Callback when value changes
   */
  onValueChange?: (value: string) => void;
  /**
   * Whether the select is disabled
   * @default false
   */
  disabled?: boolean;
  /**
   * Placeholder text
   */
  placeholder?: string;
  /**
   * Select options - can be flat array or grouped
   */
  options?: SelectOption[] | SelectGroup[];
  /**
   * Size of the select trigger
   * @default 'default'
   */
  size?: SelectSize;
  /**
   * Error state - adds error styling
   * @default false
   */
  error?: boolean;
  /**
   * Custom className for the trigger
   */
  className?: string;
  /**
   * Full width - makes select take full width of container
   * @default true
   */
  fullWidth?: boolean;
  /**
   * Custom children for advanced usage
   */
  children?: ReactNode;
  /**
   * Accessible label for the trigger. Use when there is no visible label (e.g. filters in a toolbar).
   */
  'aria-label'?: string;
}

const isGroupedOptions = (
  options: SelectOption[] | SelectGroup[]
): options is SelectGroup[] => {
  return (
    options.length > 0 &&
    'options' in options[0] &&
    Array.isArray(options[0].options)
  );
};

export const Select = forwardRef<HTMLButtonElement, SelectProps>(
  (
    {
      value,
      defaultValue,
      onValueChange,
      disabled,
      placeholder,
      options,
      size = 'default',
      error,
      className,
      fullWidth = true,
      children,
      'aria-label': ariaLabel,
    },
    ref
  ) => {
    const renderOptions = () => {
      if (!options) return null;

      if (isGroupedOptions(options)) {
        return options.map((group, groupIndex) => (
          <Fragment key={groupIndex}>
            {groupIndex > 0 && <SelectSeparator />}
            <SelectGroup>
              {group.label && <SelectLabel>{group.label}</SelectLabel>}
              {group.options.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectGroup>
          </Fragment>
        ));
      }

      return options.map((option) => (
        <SelectItem key={option.value} value={option.value}>
          {option.label}
        </SelectItem>
      ));
    };

    return (
      <ShadcnSelect
        value={value}
        defaultValue={defaultValue}
        onValueChange={onValueChange}
        disabled={disabled}
      >
        <SelectTrigger
          ref={ref}
          size={size}
          aria-invalid={error}
          aria-label={ariaLabel}
          className={cn(fullWidth && 'w-full', className)}
        >
          <SelectValue placeholder={placeholder} />
        </SelectTrigger>
        <SelectContent>{children || renderOptions()}</SelectContent>
      </ShadcnSelect>
    );
  }
);

Select.displayName = 'Select';

/**
 * Re-export select subcomponents for advanced usage
 */
export const SelectTrigger = ShadcnSelectTrigger;
export const SelectValue = ShadcnSelectValue;
export const SelectContent = ShadcnSelectContent;
export const SelectItem = ShadcnSelectItem;
export const SelectGroup = ShadcnSelectGroup;
export const SelectLabel = ShadcnSelectLabel;
export const SelectSeparator = ShadcnSelectSeparator;

export default Select;
